import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CreateWebsitePageRequest,
  CreateWebsiteRequest,
  PageRevisionSummary,
  PublishWebsiteResult,
  StoreEmailTemplate,
  StoreEmailTemplateKey,
  UpdateWebsiteDataCollectionsRequest,
  UpdateWebsiteTranslationsRequest,
  UpdateWebsitePageRequest,
  UpdateWebsiteRequest,
  UpdateStoreEmailSettingsRequest,
  UpdateStoreEmailTemplateRequest,
  WebsiteDeploymentsState,
  WebsiteDeploymentSummary,
  WebsiteDataCollectionsState,
  WebsiteEmailConfigurationState,
  WebsiteVersionRestoreResult,
  WebsiteVersionsState,
  WebsiteVersionSummary,
  WebsitePageRevisionDocumentState,
  WebsitePageRevisionRestoreResult,
  WebsitePageRevisionsState,
  WebsitePagesState,
  WebsiteTranslationsState,
  WebsitePageSummary,
  WebsiteSummary
} from '../models/website-management.models';
import { BuiltInWebsiteTemplateDefinition } from '../models/website-template.models';

export interface WebsiteManagementApiError {
  code: number;
  message: string;
  details?: string[];
}

interface WebsiteTemplatesListResponse {
  success: boolean;
  templates: BuiltInWebsiteTemplateDefinition[];
}

interface WebsitesListResponse {
  success: boolean;
  websites: WebsiteSummary[];
}

interface WebsiteResponse {
  success: boolean;
  website: WebsiteSummary;
}

interface WebsitePageResponse {
  success: boolean;
  website: WebsiteSummary;
  page: WebsitePageSummary;
}

interface WebsitePagesResponse {
  success: boolean;
  website: WebsiteSummary;
  pages: WebsitePageSummary[];
}

interface DeleteWebsiteResponse {
  success: boolean;
  deletedWebsitePublicId: string;
}

interface DeleteWebsitePageResponse {
  success: boolean;
  website: WebsiteSummary;
  pages: WebsitePageSummary[];
  deletedPagePublicId: string;
}

interface PageRevisionsResponse {
  success: boolean;
  website: WebsiteSummary;
  page: WebsitePageSummary;
  revisions: PageRevisionSummary[];
}

interface PageRevisionDocumentResponse {
  success: boolean;
  website: WebsiteSummary;
  page: WebsitePageSummary;
  revision: PageRevisionSummary;
  document: unknown;
}

interface PageRevisionRestoreResponse {
  success: boolean;
  website: WebsiteSummary;
  page: WebsitePageSummary;
  revision: PageRevisionSummary;
  revisions: PageRevisionSummary[];
}

interface WebsiteDeploymentsResponse {
  success: boolean;
  website: WebsiteSummary;
  deployments: WebsiteDeploymentSummary[];
}

interface WebsiteVersionsResponse {
  success: boolean;
  website: WebsiteSummary;
  versions: WebsiteVersionSummary[];
}

interface WebsiteVersionResponse {
  success: boolean;
  website: WebsiteSummary;
  version: WebsiteVersionSummary;
}

interface WebsiteVersionRestoreResponse {
  success: boolean;
  website: WebsiteSummary;
  pages: WebsitePageSummary[];
  version: WebsiteVersionSummary;
}

interface WebsitePublishResponse {
  success: boolean;
  website: WebsiteSummary;
  deployment: WebsiteDeploymentSummary;
}

interface WebsiteTranslationsResponse {
  success: boolean;
  website: WebsiteSummary;
  languages: string[];
  entries: WebsiteTranslationsState['entries'];
}

interface WebsiteDataCollectionsResponse {
  success: boolean;
  website: WebsiteSummary;
  collections: WebsiteDataCollectionsState['collections'];
}

interface WebsiteEmailConfigurationResponse {
  success: boolean;
  settings: WebsiteEmailConfigurationState['settings'];
  templates: StoreEmailTemplate[];
}

interface WebsiteEmailSettingsResponse {
  success: boolean;
  settings: WebsiteEmailConfigurationState['settings'];
}

interface WebsiteEmailTemplateResponse {
  success: boolean;
  template: StoreEmailTemplate;
}

interface WebsiteEmailTestResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebsiteManagementService {
  private readonly API_URL = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getWebsites(): Observable<WebsiteSummary[]> {
    return this.http
      .get<WebsitesListResponse>(`${this.API_URL}/websites`)
      .pipe(map((response) => response.websites), catchError(this.handleError));
  }

  /**
   * Built-in template catalogue. Owned by the API template registry so the two
   * apps cannot drift; this app never hard-codes the list.
   */
  getWebsiteTemplates(): Observable<BuiltInWebsiteTemplateDefinition[]> {
    return this.http
      .get<WebsiteTemplatesListResponse>(`${this.API_URL}/websites/templates`)
      .pipe(map((response) => response.templates), catchError(this.handleError));
  }

  createWebsite(payload: CreateWebsiteRequest): Observable<WebsiteSummary> {
    return this.http
      .post<WebsiteResponse>(`${this.API_URL}/websites`, payload)
      .pipe(map((response) => response.website), catchError(this.handleError));
  }

  updateWebsite(websitePublicId: string, payload: UpdateWebsiteRequest): Observable<WebsiteSummary> {
    return this.http
      .patch<WebsiteResponse>(`${this.API_URL}/websites/${websitePublicId}`, payload)
      .pipe(map((response) => response.website), catchError(this.handleError));
  }

  deleteWebsite(websitePublicId: string): Observable<string> {
    return this.http
      .delete<DeleteWebsiteResponse>(`${this.API_URL}/websites/${websitePublicId}`)
      .pipe(map((response) => response.deletedWebsitePublicId), catchError(this.handleError));
  }

  getPages(websitePublicId: string): Observable<WebsitePagesState> {
    return this.http
      .get<WebsitePagesResponse>(`${this.API_URL}/websites/${websitePublicId}/pages`)
      .pipe(
        map((response) => ({
          website: response.website,
          pages: response.pages
        })),
        catchError(this.handleError)
      );
  }

  createPage(websitePublicId: string, payload: CreateWebsitePageRequest): Observable<{ website: WebsiteSummary; page: WebsitePageSummary; }> {
    return this.http
      .post<WebsitePageResponse>(`${this.API_URL}/websites/${websitePublicId}/pages`, payload)
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page
        })),
        catchError(this.handleError)
      );
  }

  updatePage(
    websitePublicId: string,
    pagePublicId: string,
    payload: UpdateWebsitePageRequest
  ): Observable<{ website: WebsiteSummary; page: WebsitePageSummary; }> {
    return this.http
      .patch<WebsitePageResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}`, payload)
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page
        })),
        catchError(this.handleError)
      );
  }

  deletePage(websitePublicId: string, pagePublicId: string): Observable<{ website: WebsiteSummary; pages: WebsitePageSummary[]; deletedPagePublicId: string; }> {
    return this.http
      .delete<DeleteWebsitePageResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}`)
      .pipe(
        map((response) => ({
          website: response.website,
          pages: response.pages,
          deletedPagePublicId: response.deletedPagePublicId
        })),
        catchError(this.handleError)
      );
  }

  getPageRevisions(websitePublicId: string, pagePublicId: string): Observable<WebsitePageRevisionsState> {
    return this.http
      .get<PageRevisionsResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}/revisions`)
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page,
          revisions: response.revisions
        })),
        catchError(this.handleError)
      );
  }

  getPageRevision(
    websitePublicId: string,
    pagePublicId: string,
    revisionPublicId: string
  ): Observable<WebsitePageRevisionDocumentState> {
    return this.http
      .get<PageRevisionDocumentResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}/revisions/${revisionPublicId}`)
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page,
          revision: response.revision,
          document: response.document
        })),
        catchError(this.handleError)
      );
  }

  restorePageRevision(
    websitePublicId: string,
    pagePublicId: string,
    revisionPublicId: string
  ): Observable<WebsitePageRevisionRestoreResult> {
    return this.http
      .post<PageRevisionRestoreResponse>(`${this.API_URL}/websites/${websitePublicId}/pages/${pagePublicId}/revisions/${revisionPublicId}/restore`, {})
      .pipe(
        map((response) => ({
          website: response.website,
          page: response.page,
          revision: response.revision,
          revisions: response.revisions
        })),
        catchError(this.handleError)
      );
  }

  getDeployments(websitePublicId: string): Observable<WebsiteDeploymentsState> {
    return this.http
      .get<WebsiteDeploymentsResponse>(`${this.API_URL}/websites/${websitePublicId}/deployments`)
      .pipe(
        map((response) => ({
          website: response.website,
          deployments: response.deployments
        })),
        catchError(this.handleError)
      );
  }

  publishWebsite(websitePublicId: string): Observable<PublishWebsiteResult> {
    return this.http
      .post<WebsitePublishResponse>(`${this.API_URL}/websites/${websitePublicId}/publish`, {})
      .pipe(
        map((response) => ({
          website: response.website,
          deployment: response.deployment
        })),
        catchError(this.handleError)
      );
  }

  getWebsiteVersions(websitePublicId: string): Observable<WebsiteVersionsState> {
    return this.http
      .get<WebsiteVersionsResponse>(`${this.API_URL}/websites/${websitePublicId}/versions`)
      .pipe(
        map((response) => ({ website: response.website, versions: response.versions })),
        catchError(this.handleError)
      );
  }

  createWebsiteVersion(websitePublicId: string, note?: string): Observable<{ website: WebsiteSummary; version: WebsiteVersionSummary }> {
    return this.http
      .post<WebsiteVersionResponse>(`${this.API_URL}/websites/${websitePublicId}/versions`, { note })
      .pipe(
        map((response) => ({ website: response.website, version: response.version })),
        catchError(this.handleError)
      );
  }

  restoreWebsiteVersion(websitePublicId: string, versionPublicId: string): Observable<WebsiteVersionRestoreResult> {
    return this.http
      .post<WebsiteVersionRestoreResponse>(`${this.API_URL}/websites/${websitePublicId}/versions/${versionPublicId}/restore`, {})
      .pipe(
        map((response) => ({
          website: response.website,
          pages: response.pages,
          version: response.version
        })),
        catchError(this.handleError)
      );
  }

  getTranslations(websitePublicId: string): Observable<WebsiteTranslationsState> {
    return this.http
      .get<WebsiteTranslationsResponse>(`${this.API_URL}/websites/${websitePublicId}/translations`)
      .pipe(
        map((response) => ({
          website: response.website,
          languages: response.languages,
          entries: response.entries
        })),
        catchError(this.handleError)
      );
  }

  updateTranslations(
    websitePublicId: string,
    payload: UpdateWebsiteTranslationsRequest
  ): Observable<WebsiteTranslationsState> {
    return this.http
      .put<WebsiteTranslationsResponse>(`${this.API_URL}/websites/${websitePublicId}/translations`, payload)
      .pipe(
        map((response) => ({
          website: response.website,
          languages: response.languages,
          entries: response.entries
        })),
        catchError(this.handleError)
      );
  }

  getDataCollections(websitePublicId: string): Observable<WebsiteDataCollectionsState> {
    return this.http
      .get<WebsiteDataCollectionsResponse>(`${this.API_URL}/websites/${websitePublicId}/data-collections`)
      .pipe(
        map((response) => ({
          website: response.website,
          collections: response.collections
        })),
        catchError(this.handleError)
      );
  }

  updateDataCollections(
    websitePublicId: string,
    payload: UpdateWebsiteDataCollectionsRequest
  ): Observable<WebsiteDataCollectionsState> {
    return this.http
      .put<WebsiteDataCollectionsResponse>(`${this.API_URL}/websites/${websitePublicId}/data-collections`, payload)
      .pipe(
        map((response) => ({
          website: response.website,
          collections: response.collections
        })),
        catchError(this.handleError)
      );
  }

  getEmailConfiguration(websitePublicId: string): Observable<WebsiteEmailConfigurationState> {
    return this.http
      .get<WebsiteEmailConfigurationResponse>(`${this.API_URL}/_bw-store/${websitePublicId}/admin/email-settings`)
      .pipe(
        map((response) => ({
          settings: response.settings,
          templates: response.templates
        })),
        catchError(this.handleError)
      );
  }

  updateEmailSettings(
    websitePublicId: string,
    payload: UpdateStoreEmailSettingsRequest
  ): Observable<WebsiteEmailConfigurationState['settings']> {
    return this.http
      .patch<WebsiteEmailSettingsResponse>(`${this.API_URL}/_bw-store/${websitePublicId}/admin/email-settings`, payload)
      .pipe(map((response) => response.settings), catchError(this.handleError));
  }

  updateEmailTemplate(
    websitePublicId: string,
    templateKey: StoreEmailTemplateKey,
    payload: UpdateStoreEmailTemplateRequest
  ): Observable<StoreEmailTemplate> {
    return this.http
      .patch<WebsiteEmailTemplateResponse>(
        `${this.API_URL}/_bw-store/${websitePublicId}/admin/email-templates/${templateKey}`,
        payload
      )
      .pipe(map((response) => response.template), catchError(this.handleError));
  }

  sendEmailTest(websitePublicId: string, to: string, templateKey: StoreEmailTemplateKey): Observable<string> {
    return this.http
      .post<WebsiteEmailTestResponse>(`${this.API_URL}/_bw-store/${websitePublicId}/admin/email-settings/test`, {
        to,
        templateKey
      })
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined
    } as WebsiteManagementApiError));
  };
}
